import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1098CDueDates = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 1098-C deadlines for the Tax Year 2025 | TaxZerone"
          description="Learn the Form 1098-C deadline for Tax Year 2025. Stay compliant by meeting IRS due dates for reporting donated vehicles, boats, and airplanes with TaxZerone."
          keywords=""
          canonical="resources/information-returns/form-1098-c-due-dates/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1098-C</span>
          </span>{' '}
          Due Dates
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Learn about the key due date for form 1098-C and Act fast!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098Cduedates`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1098-c-due-dates/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1098-C due dates at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        {
                          'Donee organizations receiving qualified vehicle contributions must '
                        }
                        <Link
                          href="/information-returns/efile-irs-form-1098-c/"
                          sx={{ color: '#0202ff' }}
                        >
                          file Form 1098-C
                        </Link>
                        {' with the IRS and provide a copy to donors.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Filing Deadlines:
                        </strong>
                        {' Paper Form 1098-C filings are '}
                        generally due
                        {' by '}
                        <strong style={{ fontWeight: 600 }}>February 28</strong>
                        {', electronic filings are due by '}
                        <strong style={{ fontWeight: 600 }}>March 31</strong>
                        {', and donor copies must be '}
                        furnished
                        {' within '}
                        <strong style={{ fontWeight: 600 }}>30 days</strong>
                        <strong style={{ fontWeight: 600 }}>
                          {' of the vehicle contribution or sale'}
                        </strong>
                        .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Penalties:</strong>
                        {
                          ' Late, incorrect, or missing Form 1098-C filings may result in IRS penalties based on the level of noncompliance.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        File Form 1098-C
                        {
                          ' before the deadline to avoid IRS penalties and maintain accurate donation reporting.'
                        }
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS form 1098-C?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  {' '}
                  Form 1098-C Contributions of Motor Vehicles, Boats, and
                  Airplanes are used by donee organizations to report the
                  receipt of qualified vehicle donations to the IRS and the
                  donor. This form provides critical details about the donated
                  vehicle, including its fair market value, whether it was sold
                  or used for charitable purposes, and any applicable tax
                  benefits for the donor.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Filing Deadlines for Form 1098-C
                </Typography>

                <Typography variant="body1" component="div" sx={{ mb: 2 }}>
                  The IRS has established specific deadlines for{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1098-c/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    filing Form 1098-C
                  </Link>{' '}
                  and providing a copy to the donor. Organizations must comply
                  with these deadlines to avoid potential penalties.
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Filing type
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Deadline Date
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Paper filing
                          </TableCell>

                          <TableCell sx={{ fontWeight: 600 }}>
                            March 2, 2026{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Electronic filing
                          </TableCell>

                          <TableCell sx={{ fontWeight: 600 }}>
                            March 31, 2026{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Sending Donor copies
                          </TableCell>

                          <TableCell sx={{ fontWeight: 600 }}>
                            30 days from date of sale or contribution{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Penalties:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS imposes penalties for furnishing false or fraudulent
                  acknowledgments, failing to file correct information returns,
                  and failing to furnish correct payee statements. Below are the
                  penalty details:{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Fraudulent Acknowledgments (Section 6720):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If a donee organization knowingly furnishes a false or
                  fraudulent Form 1098-C or fails to provide the acknowledgment
                  within 30 days, penalties apply as follows:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li style={{ fontWeight: 600 }}>
                    For a qualified vehicle where Box 4a is checked:{' '}
                  </li>
                  <ul>
                    <li>The penalty is the greater of: </li>
                    <ul>
                      <li>The complete sales proceeds from the vehicle, or </li>
                      <li>
                        The sales price stated in the acknowledgment multiplied
                        by 39.6% (0.396).{' '}
                      </li>
                    </ul>
                  </ul>
                  <li style={{ fontWeight: 600 }}>
                    For a qualified vehicle where Box 5a or 5b is checked:{' '}
                  </li>
                  <ul>
                    <li>The penalty is the greater of: </li>
                    <ul>
                      <li>$5,000, or </li>
                      <li>
                        The claimed value of the vehicle multiplied by 39.6%
                        (0.396).
                      </li>
                    </ul>
                  </ul>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Failure to File or Furnish Correct Information (Sections 6721
                  & 6722)
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    A penalty applies if Form 1098-C is not{' '}
                    <span style={{ fontWeight: 600 }}> filed with the IRS</span>{' '}
                    or{' '}
                    <span style={{ fontWeight: 600 }}>
                      furnished to the donor
                    </span>{' '}
                    by the required due date.{' '}
                  </li>
                  <li>
                    The penalty amount varies based on the{' '}
                    <span style={{ fontWeight: 600 }}>
                      extent of noncompliance
                    </span>{' '}
                    and whether the failure was due to{' '}
                    <span style={{ fontWeight: 600 }}>
                      intentional disregard
                    </span>
                    .{' '}
                  </li>
                </Box>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Filing Timeframe
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Penalty per Return
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Maximum Penalty (Large Businesses)
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Maximum Penalty (Small Businesses)
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Filed within 30 days after due date
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            $60
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            $683,000
                          </TableCell>

                          <TableCell sx={{ textAlign: 'center' }}>
                            $239,000
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Filed more than 30 days late but by August 1
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            $130
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            $2,049,000
                          </TableCell>

                          <TableCell sx={{ textAlign: 'center' }}>
                            $683,000
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Filed after August 1 or not filed at all
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            $340
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            $4,098,500
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            $1,366,000
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Intentional disregard
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            $680
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            No limit
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            No limit
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided in this resource is based on
                        the latest Internal Revenue Service (IRS) guidelines.
                        For more details, please refer to the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i1098c.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          IRS instructions on Form 1098-C
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1099gi"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          General instruction of Information Returns
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              File your Form 1098-C with TaxZerone
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              Ensure timely compliance by filing Form 1098-C before the due
              date.
              <br />
              Avoid penalties and stay IRS-compliant.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098Cduedates`}
              >
                E-file Form 1098-C Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1098CDueDates;
